import { useCallback, useEffect, useRef, useState } from "react";
import http from "../../Utils/axiosConfig";
import "./AdBanner.css";

// Where the media is served from. The API serves /uploads from outside its own deploy
// directory; the panel only ever needs the origin, not the /api prefix.
const MEDIA_ORIGIN = (process.env.REACT_APP_API_BASE_URL || "http://localhost:3001/api/user")
  .replace(/\/api\/.*$/, "");

// The login page has no session, so the only thing we can say about the viewer is which
// dairy they logged into last on this device. Written at login and deliberately kept
// through logout; absent on a first visit, which simply means global ads.
export const LAST_DAIRY_KEY = "last_dairy_id";

// How long an image stays up. A video instead advances when it ends, so an advertiser
// gets their whole clip — with this as a backstop in case `ended` never fires.
const IMAGE_MS = 5000;
const VIDEO_CAP_MS = 30000;

const AdBanner = () => {
  const [ads, setAds] = useState([]);
  const [index, setIndex] = useState(0);
  const [zoom, setZoom] = useState(false);
  // One impression per ad per page view. Without this, a login page left open would
  // keep re-counting the same ads every loop and inflate what you invoice on.
  const countedRef = useRef(new Set());

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const hint = localStorage.getItem(LAST_DAIRY_KEY);
        const res = await http.get("ads", { params: hint ? { mst_id: hint } : {} });
        const list = res?.code === "DATA_FOUND" ? res.data.ads || [] : [];
        if (!cancelled) setAds(list);
      } catch (err) {
        // Deliberately swallowed. An advert must never be the reason a farmer
        // cannot see the login form.
        console.debug("ad fetch skipped:", err?.message);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const current = ads[index] || null;

  // Count the slide that is actually on screen, not the whole set up front — that is
  // what makes the impression numbers mean "someone saw this one".
  useEffect(() => {
    if (!current || countedRef.current.has(current.id)) return;
    countedRef.current.add(current.id);
    const hint = localStorage.getItem(LAST_DAIRY_KEY);
    http.post(`ads/${current.id}/impression`, hint ? { mst_id: hint } : {}).catch(() => {});
  }, [current]);

  const next = useCallback(() => {
    setIndex((i) => (ads.length ? (i + 1) % ads.length : 0));
  }, [ads.length]);

  // Images advance on a timer. Video advances on `ended` (below), with this as a cap.
  useEffect(() => {
    if (ads.length < 2 || !current || zoom) return;
    const wait = current.media_type === "video" ? VIDEO_CAP_MS : IMAGE_MS;
    const timer = setTimeout(next, wait);
    return () => clearTimeout(timer);
  }, [current, ads.length, next, zoom]);

  // Escape closes the full-screen view, as any modal should.
  useEffect(() => {
    if (!zoom) return;
    const onKey = (e) => e.key === "Escape" && setZoom(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom]);

  // Renders nothing at all until there is something to show — no placeholder box,
  // so the login form never shifts under the farmer's thumb.
  if (!current) return null;

  const isYouTube = current.media_type === "youtube";
  const isImage = !isYouTube && current.media_type !== "video";

  // An image is always tappable — it opens full screen, which is worth doing even
  // without a click_url. A YouTube slide never is: the iframe swallows the tap, so a
  // pointer cursor and a link role would promise something that cannot happen.
  const clickable = isImage || (!!current.click_url && !isYouTube);

  const handleClick = () => {
    const hint = localStorage.getItem(LAST_DAIRY_KEY);
    http.post(`ads/${current.id}/click`, hint ? { mst_id: hint } : {}).catch(() => {});
    // An image opens full screen and offers click_url as a button there, so the farmer
    // is never thrown out to a browser by a stray tap on the login page.
    if (isImage) setZoom(true);
    else if (current.click_url) window.open(current.click_url, "_blank", "noopener,noreferrer");
  };

  // For a YouTube ad, file_path holds the video id rather than a path.
  // nocookie: this is the login screen of an app holding a farmer's financial records,
  // so the embed should not drop YouTube's tracking cookies on their device.
  // mute=1 is required for autoplay; loop on YouTube needs playlist=<id> as well.
  const src = isYouTube
    ? `https://www.youtube-nocookie.com/embed/${current.file_path}` +
      `?autoplay=1&mute=1&playsinline=1&controls=0&rel=0&modestbranding=1` +
      (ads.length === 1 ? `&loop=1&playlist=${current.file_path}` : "")
    : `${MEDIA_ORIGIN}/uploads/${current.file_path}`;

  return (
    <div className="ad-carousel">
      <div
        className={`ad-slot ${clickable ? "ad-slot--clickable" : ""} ${isImage ? "ad-slot--zoom" : ""}`}
        onClick={clickable ? handleClick : undefined}
        role={clickable ? (isImage ? "button" : "link") : undefined}
        tabIndex={clickable ? 0 : undefined}
        onKeyDown={clickable ? (e) => e.key === "Enter" && handleClick() : undefined}
      >
        {isYouTube ? (
          // The iframe swallows taps, so click tracking is not possible here and
          // click_url is ignored for these ads — stated in the Super Admin form.
          // It also gives no `ended` event, so the slide advances on the timer above.
          <iframe
            key={current.id}
            src={src}
            title={current.title}
            className="ad-media ad-media--embed"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        ) : current.media_type === "video" ? (
          // muted is not optional: mobile browsers refuse to autoplay with sound, and
          // playsInline stops iOS taking over the screen.
          //
          // loop only when it is the ONLY ad. With several, `ended` hands over to the
          // next advertiser instead. Without that condition a lone video froze on its
          // last frame: next() computes (0 + 1) % 1 = 0, the index never changes, so the
          // element is never remounted and nothing restarts it.
          <video
            key={current.id}
            src={src}
            muted
            autoPlay
            playsInline
            loop={ads.length === 1}
            preload="metadata"
            className="ad-media"
            onEnded={ads.length > 1 ? next : undefined}
          />
        ) : (
          // key forces a fresh element per slide, so the fade restarts each time.
          <img key={current.id} src={src} alt={current.title} className="ad-media" />
        )}
        <span className="ad-label">जाहिरात</span>
      </div>

      {/* Only worth drawing when there is actually more than one. Tappable, so a
          farmer who wants the other advert does not have to wait for it. */}
      {ads.length > 1 && (
        <div className="ad-dots">
          {ads.map((ad, i) => (
            <button
              key={ad.id}
              type="button"
              className={`ad-dot ${i === index ? "ad-dot--on" : ""}`}
              aria-label={`जाहिरात ${i + 1}`}
              onClick={() => setIndex(i)}
            />
          ))}
        </div>
      )}

      {/* Clicking anywhere closes it; only the advertiser's button stops the bubble. */}
      {zoom && isImage && (
        <div
          className="ad-zoom"
          role="dialog"
          aria-modal="true"
          aria-label={current.title}
          onClick={() => setZoom(false)}
        >
          <button type="button" className="ad-zoom-close" aria-label="बंद करा">×</button>
          <img src={src} alt={current.title} />
          {current.click_url && (
            <button
              type="button"
              className="ad-zoom-cta"
              onClick={(e) => {
                e.stopPropagation();
                window.open(current.click_url, "_blank", "noopener,noreferrer");
              }}
            >
              अधिक माहिती →
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AdBanner;
